/**
 * Libellés d'écran des correspondances entre concepts (chantier C23 « Confrontation »,
 * SP4, `VOCABULAIRE_ECRAN.md`) : ce que chacun dit de la relation entre son concept et
 * celui de l'autre. Les cinq relations portent les mots de la décision 10 du cadrage ;
 * une ligne se lit du point de vue de qui l'a posée (« Vous : … », « Claire : … »),
 * sans verdict. L'écran ne dit jamais « alignement », « match », « SKOS »,
 * « confrontation », « cible », « provenance ». Testés contre `motsBannis`.
 */
import type { RelationAlignement } from '../../../types/api';

const guillemets = (s: string): string => `« ${s} »`;

/** Nom d'une personne, ou le repli. */
const personne = (nom: string): string => nom || 'Un lecteur';

export const CORRESPONDANCE_LABELS = {
  /** Rubrique du face à face ; bloc des fiches, colonne et section du Dictionnaire. */
  rubrique: 'Correspondance',
  bloc: 'Correspondances',
  /** Les cinq relations, telles qu'elles se choisissent : ce que le concept de l'autre est par rapport au mien. */
  relations: {
    exactMatch: 'Correspond exactement',
    closeMatch: 'Correspond à peu près',
    broadMatch: 'Plus général',
    narrowMatch: 'Plus précis',
    relatedMatch: 'En rapport',
  } as Record<RelationAlignement, string>,
  /** La même relation en phrase, du point de vue de qui l'a posée : « S … C ». */
  relationsEnPhrase: {
    exactMatch: 'correspond exactement à',
    closeMatch: 'correspond à peu près à',
    broadMatch: 'est plus précis que',
    narrowMatch: 'est plus général que',
    relatedMatch: 'est en rapport avec',
  } as Record<RelationAlignement, string>,
  /** « « libre arbitre » correspond à peu près à « liberté » ». */
  phrase: (source: string, relation: RelationAlignement, cible: string): string =>
    `${guillemets(source)} ${CORRESPONDANCE_LABELS.relationsEnPhrase[relation]} ${guillemets(cible)}`,
  /** Chez qui se trouve le concept visé (le nom de l'autre personne ou de l'organisation, tel que le serveur le rend). */
  chez: (nom: string): string => `chez ${nom}`,
  /** Qui parle : « Vous », ou le prénom. */
  vous: 'Vous',
  auteur: (nom: string): string => personne(nom),
  /** Le titre du geste : ce que le concept de l'autre est par rapport au mien. */
  titreGeste: (maCle: string, saCle: string, prenom: string): string =>
    `${guillemets(saCle)} de ${personne(prenom)} est, par rapport à votre ${guillemets(maCle)} :`,
  groupeRelations: 'Comment les deux concepts se correspondent',
  pourquoi: 'Pourquoi ?',
  pourquoiAide: 'Facultatif : ce qui vous fait dire cela.',
  /** Boutons. */
  dire: 'Dire comment ils se correspondent',
  enregistrer: 'Enregistrer',
  modifier: 'Modifier',
  retirer: 'Retirer',
  annuler: 'Annuler',
  /** États. */
  aucune: 'Aucune correspondance pour l’instant.',
  invitation: 'Mettez un de vos concepts en face pour dire comment ils se correspondent.',
  indisponible: 'Ce concept n’est plus disponible.',
  echec: 'Enregistrement impossible. Réessayez.',
  echecRetrait: 'Retrait impossible. Réessayez.',
  /** Infobulle de la date d'une de mes lignes. */
  poseLe: (date: string): string => `Posé le ${date}`,
  /** Graphe : pastille de la famille d'arêtes. */
  graphe: 'Correspondance',
  /** Nom accessible de la liste. */
  liste: 'Correspondances entre concepts',
  /** « 1 correspondance », « 3 correspondances » (récapitulatif du Dictionnaire). */
  compte: (n: number): string => `${n} correspondance${n > 1 ? 's' : ''}`,
  /** Une ligne du Dictionnaire : « correspond à peu près à « Liberté » (Claire) — Pourquoi ». */
  ligneExport: (relation: RelationAlignement, cible: string, personne: string, pourquoi?: string): string =>
    `${CORRESPONDANCE_LABELS.relationsEnPhrase[relation]} ${guillemets(cible)} (${personne || 'un lecteur'})${pourquoi ? ` — ${pourquoi}` : ''}`,
} as const;

export default CORRESPONDANCE_LABELS;
